import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Read/write a single query-string key as if it were React state.
 *
 *   const [section, setSection] = useUrlParam("section", { replace: true });
 *   setSection("forms");                       // ?section=forms
 *   setSection(null);                          // remove the param
 *   setSection("a", { clear: ["form"] });      // ?section=a (drops ?form)
 *
 * Options:
 *  - replace:      use history.replace by default (no new history entry)
 *  - defaultValue: returned when the param is absent
 */
const isEmpty = (v) => v === undefined || v === null || v === "";

const useUrlParam = (key, { defaultValue, replace = false } = {}) => {
  const [params, setParams] = useSearchParams();
  const value = params.get(key) ?? defaultValue;

  const setValue = useCallback(
    (next, { clear, replace: replaceOverride } = {}) => {
      setParams(
        (prev) => {
          const draft = new URLSearchParams(prev);
          if (isEmpty(next)) draft.delete(key);
          else draft.set(key, String(next));
          clear?.forEach((k) => draft.delete(k));
          return draft;
        },
        { replace: replaceOverride ?? replace }
      );
    },
    [key, replace, setParams]
  );

  return [value, setValue];
};

export default useUrlParam;
